import { useEffect, useState } from "react";
import { Timeline, type TimelineStep, type TimelineStepState } from "components/common/Timeline";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Badge,
  Card,
  CardBody,
  Col,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import AmcLineItemsTab from "./AmcLineItemsTab";
import { Link } from "react-router-dom";
import StatusBadge from "components/common/StatusBadge";
import { DateOnly } from "components/common/Date";
import {
  AmcContractStatus,
  AmcSourceType,
  AmcSourceTypeLabelMap,
  amcStatusMap,
} from "features/Amc/constants/amc-contract.constants";
import { formatCurrency } from "utils/currency";
import AssignmentsOverviewCard from "features/WorkAssignments/components/AssignmentsOverviewCard";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useAmcContractOverview } from "./AmcContractOverviewContext";
import { getInvoices } from "features/Invoices/services/InvoiceService";
import {
  InvoiceSourceType,
  InvoiceStatus,
  InvoiceStatusMap,
} from "features/Invoices/constants/invoice.constants";
import type { Invoice } from "features/Invoices/types/invoice.types";
import type { Amc } from "features/Amc/types/amc.types";
import ClientAdvancesCard from "features/ClientAdvances/components/ClientAdvancesCard";
import { ClientPopover } from "components/common/ClientPopover";
import { ClientAdvanceSourceType } from "features/ClientAdvances/constants/client-advance.constants";

function buildAmcTimeline(amc: Amc): TimelineStep[] {
  const history = amc.statusHistory ?? [];
  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const label = amcStatusMap[entry.status as AmcContractStatus]?.label ?? entry.status;
    const state: TimelineStepState = isLast
      ? entry.status === AmcContractStatus.CANCELLED
        ? "skipped"
        : entry.status === AmcContractStatus.COMPLETED
          ? "done"
          : "active"
      : "done";
    return {
      label,
      timestamp: entry.at,
      by: entry.user
        ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
        : entry.by != null ? `#${entry.by}` : undefined,
      actor: entry.message ?? undefined,
      state,
    };
  });
}

function InvoiceCard({
  sourceId,
  updatedAt,
}: {
  sourceId: number;
  /** Re-fetches whenever the contract changes — e.g. after regenerate-invoice,
   * which may replace the active invoice or correct it in place. */
  updatedAt?: string;
}) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getInvoices({ sourceType: InvoiceSourceType.AMC, sourceId, limit: 10 })
      .then((res) => {
        if (res.success) setInvoices(res.data.rows);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sourceId, updatedAt]);

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center py-3">
          <Spinner size="sm" />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="text-uppercase mb-0">Invoices</h6>
          <span className="badge bg-light text-dark">{invoices.length}</span>
        </div>
        {invoices.length === 0 ? (
          <p className="text-muted small mb-0">No invoices raised yet.</p>
        ) : (
          <div className="vstack gap-2">
            {invoices.map((inv) => {
              const statusInfo = InvoiceStatusMap[inv.status];
              return (
                <div
                  key={inv.id}
                  className="d-flex align-items-center justify-content-between"
                >
                  <div className="d-flex flex-column gap-1">
                    <Link
                      to={`/finance/invoices/${inv.id}`}
                      className="fw-medium text-body text-decoration-none link"
                    >
                      {inv.code || `Invoice #${inv.id}`}
                    </Link>
                    {inv.issueDate ? (
                      <span className="text-muted small">
                        Issued: <DateOnly date={inv.issueDate} />
                      </span>
                    ) : null}
                    {inv.status === InvoiceStatus.CANCELLED &&
                    inv.cancelReason ? (
                      <span className="text-muted small">
                        Reason: {inv.cancelReason}
                      </span>
                    ) : null}
                  </div>
                  <div className="d-flex flex-column align-items-end gap-1">
                    <Badge
                      color={statusInfo?.color ?? "secondary"}
                      className="fs-11"
                    >
                      {statusInfo?.label ?? inv.status}
                    </Badge>
                    {inv.status === InvoiceStatus.CANCELLED &&
                    inv.cancelledAt ? (
                      <span className="text-muted small">
                        <DateOnly date={inv.cancelledAt} />
                      </span>
                    ) : null}
                    {inv.outstandingAmount > 0 ? (
                      <span className="text-muted small">
                        Due: {formatCurrency(inv.outstandingAmount)}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function isTerminalAmcStatus(status: string | null | undefined): boolean {
  const s = String(status ?? "")
    .trim()
    .toUpperCase();
  return s === AmcContractStatus.CANCELLED || s === AmcContractStatus.COMPLETED;
}

export default function AmcOverviewTab() {
  const { contract, refetch } = useAmcContractOverview();
  const { can } = useAccess();
  const canCreateAssignment = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canUpdateAssignment = can(Permissions.WORK_ASSIGNMENT.UPDATE);
  const [lineItemsOpen, setLineItemsOpen] = useState("line-items");
  const toggleLineItems = (id: string) =>
    setLineItemsOpen((prev) => (prev === id ? "" : id));
  if (!contract) return null;

  const record = contract;
  const client = record.client;
  const clientName = client
    ? client.name?.trim() || client.company?.trim() || client.email || "—"
    : "—";

  return (
    <>
      <Row className="mb-0">
        <Col lg={8}>
          <Card className="mb-4">
            <CardBody>
              <h6 className="mb-3 text-uppercase">
                Annual Maintenance Contract info
              </h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    {record.code ? (
                      <tr>
                        <th
                          className="ps-0"
                          scope="row"
                          style={{ width: "200px" }}
                        >
                          Code
                        </th>
                        <td className="text-muted">{record.code}</td>
                      </tr>
                    ) : null}
                    <tr>
                      <th className="ps-0" scope="row">
                        Status
                      </th>
                      <td>
                        <StatusBadge value={record.status} map={amcStatusMap} />
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Source Type
                      </th>
                      <td className="text-muted">
                        {AmcSourceTypeLabelMap[
                          record.sourceType as AmcSourceType
                        ] ?? record.sourceType}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Source
                      </th>
                      <td className="text-muted">
                        {(() => {
                          const src = record.source;
                          if (!src?.id) return "—";
                          const sourceUrlMap: Partial<
                            Record<AmcSourceType, string>
                          > = {
                            [AmcSourceType.QUOTATION]: "/quotations",
                          };
                          const basePath = sourceUrlMap[record.sourceType];
                          const label = src.code?.trim() || `#${src.id}`;
                          if (!basePath) return label;
                          return (
                            <Link
                              to={`${basePath}/${src.id}`}
                              className="link fw-medium"
                            >
                              {label}
                            </Link>
                          );
                        })()}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Agreed amount
                      </th>
                      <td className="text-muted">
                        {formatCurrency(record.agreedAmount)}
                      </td>
                    </tr>
                    {record.finalAmount != null ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Final amount
                        </th>
                        <td className="text-muted">
                          {formatCurrency(record.finalAmount)}
                        </td>
                      </tr>
                    ) : null}
                    <tr>
                      <th className="ps-0" scope="row">
                        Agreed visits
                      </th>
                      <td className="text-muted">{record.numberOfVisits}</td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Extra visits
                      </th>
                      <td className="text-muted">
                        {record.extraVisitCount ?? 0}
                      </td>
                    </tr>
                    {/* <tr>
                      <th className="ps-0" scope="row">
                        Billing type
                      </th>
                      <td className="text-muted">
                        {AmcBillingTypeLabelMap[
                          (record.billingType as AmcBillingType) ??
                            AmcBillingType.FULL_UPFRONT
                        ] ??
                          record.billingType ??
                          "—"}
                      </td>
                    </tr> */}
                    <tr>
                      <th className="ps-0" scope="row">
                        Start date
                      </th>
                      <td className="text-muted">
                        <DateOnly date={record.startDate} />
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        End date
                      </th>
                      <td className="text-muted">
                        <DateOnly date={record.endDate} />
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Client
                      </th>
                      <td className="text-muted">
                        {record.clientId != null && client ? (
                          <ClientPopover client={client} clientId={record.clientId}>
                            <Link
                              to={`/clients/${record.clientId}`}
                              className="link fw-medium"
                            >
                              {clientName}
                            </Link>
                          </ClientPopover>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    {record.deleted_at != null && record.deleted_at !== "" ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Record
                        </th>
                        <td>
                          <span className="badge bg-danger">Deleted</span>
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>

          <Accordion
            open={lineItemsOpen}
            toggle={toggleLineItems}
            className="mb-4"
          >
            <AccordionItem>
              <AccordionHeader targetId="line-items">
                <span className="fw-medium">Line items</span>
              </AccordionHeader>
              <AccordionBody accordionId="line-items">
                <AmcLineItemsTab />
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <InvoiceCard sourceId={record.id} updatedAt={record.updated_at} />
        </Col>

        <Col lg={4}>
          <Card className="mb-3">
            <CardBody>
              <h6 className="text-uppercase mb-3 text-muted">Timeline</h6>
              <Timeline steps={buildAmcTimeline(record)} />
            </CardBody>
          </Card>
          <div className="mb-3">
            <AssignmentsOverviewCard
              sourceType={WorkAssignmentSourceType.AMC}
              sourceId={record.id}
              sourceLabel={
                record.code
                  ? `AMC ${record.code}`
                  : record.title?.trim()
                    ? `AMC: ${record.title}`
                    : `AMC #${record.id}`
              }
              canAssign={canCreateAssignment && !isTerminalAmcStatus(record.status)}
              canReassign={canUpdateAssignment && !isTerminalAmcStatus(record.status)}
              onSuccess={() => void refetch()}
            />
          </div>
          <ClientAdvancesCard
            sourceType={ClientAdvanceSourceType.AMC}
            sourceId={record.id}
          />
        </Col>
      </Row>
    </>
  );
}
